import { Popover } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/outline";
import React from "react";
import { twMerge } from "tailwind-merge";

import { Label, PopoverPanel } from "@fronterahealth/frontera-ui-components";

import { useGetReportDomainsQuery } from "@api/graphql/types-and-hooks";

interface GoalDomainTagProps {
  reportId: string;
  domainId?: string | null;
  onDomainChange?: (id: string) => void;
  isReadOnly?: boolean;
  isLoading?: boolean;
}

const TagDisplay: React.FC<{ label?: string | null; isLoading?: boolean }> = ({ label, isLoading }) => (
  <div
    className={twMerge(
      "shrink-0 rounded-sm border border-limestonev2-500 px-1",
      isLoading && "animate-pulse bg-limestonev2-300",
    )}
  >
    <Label size="extra-small" displayType="loud" className="leading-4 text-limestonev2-800">
      {label ?? "-"}
    </Label>
  </div>
);

export const GoalDomainTag: React.FC<GoalDomainTagProps> = ({
  reportId,
  domainId,
  onDomainChange,
  isReadOnly,
  isLoading,
}) => {
  const { data } = useGetReportDomainsQuery({ reportId }, { enabled: !!reportId });
  const domains = data?.getReportDomains ?? [];
  const currentLabel = domains.find((d) => d.id === domainId)?.name ?? "No domain";

  const isEditable = !isReadOnly && !!onDomainChange;

  if (!isEditable || isLoading) {
    return <TagDisplay label={currentLabel} isLoading={isLoading} />;
  }

  const popoverContent = (
    <div className="flex flex-col py-1 -m-3">
      {domains.map((domain) => (
        <div key={domain.id} className="px-2">
          <Popover.Button
            as="button"
            className="flex w-full items-center justify-between rounded-sm p-1 hover:bg-limestone-50"
            onClick={() => onDomainChange(domain.id)}
          >
            <TagDisplay label={domain.name} />
            {domain.id === domainId && <CheckIcon className="h-4 w-4 shrink-0 text-green-600" />}
          </Popover.Button>
        </div>
      ))}
    </div>
  );

  return (
    <PopoverPanel direction="bottom-left" width="min-w-[230px]" height="max-h-none" content={popoverContent}>
      <TagDisplay label={currentLabel} />
    </PopoverPanel>
  );
};
